import { Controller } from "@hotwired/stimulus"
import { serviceLibList, serviceLibAdd, serviceLibRemove, serviceLibUpdate, serviceLibCategories, serviceLibReplace } from "goal_library"
import { toCSV, parseCSV, downloadCSV } from "csv_io"

const CSV_COLUMNS = [{ key: "name", header: "Service" }, { key: "category", header: "Sub-section" }]

// Settings → Services: manage the custom services the user added to the resource
// picker's Service dropdown. Grouped by sub-section (category); add / edit / move
// / delete. Same LOCAL store as the goal library (PCPDesk: OneDrive-synced;
// browser: localStorage) — nothing on the server.
export default class extends Controller {
  static targets = ["list", "empty", "count"]

  connect() { this.render() }

  render() {
    const list = serviceLibList()
    if (this.hasCountTarget) this.countTarget.textContent = list.length ? `${list.length}` : ""
    if (this.hasEmptyTarget) this.emptyTarget.hidden = list.length > 0
    if (!this.hasListTarget) return
    const byCat = {}
    list.forEach(s => { (byCat[s.category] ||= []).push(s) })
    this.listTarget.innerHTML = Object.keys(byCat).sort().map(cat => this._catHtml(cat, byCat[cat])).join("")
  }

  add() { this._openModal({ name: "", category: "Other" }, false) }

  edit(event) {
    const name = event.currentTarget.dataset.name
    const s = serviceLibList().find(x => x.name === name)
    if (s) this._openModal(s, true)
  }

  remove(event) {
    const name = event.currentTarget.dataset.name
    if (name && window.confirm(`Remove "${name}" from your services?`)) { serviceLibRemove(name); this.render() }
  }

  // ---- Excel (CSV) round-trip ----

  exportCsv() {
    downloadCSV(toCSV(serviceLibList(), CSV_COLUMNS), "pcp-services.csv")
  }

  importCsv(event) {
    const file = event.currentTarget.files && event.currentTarget.files[0]
    event.currentTarget.value = "" // allow re-picking the same file
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      const rows = parseCSV(e.target.result).map(r => ({
        name:     (r["Service"] ?? r["name"] ?? "").trim(),
        category: (r["Sub-section"] ?? r["category"] ?? "Other").trim() || "Other",
      })).filter(r => r.name)
      if (!rows.length) { window.alert("No services found in that file. Expected a 'Service' column."); return }
      if (!window.confirm(`Replace your services with the ${rows.length} row(s) from this file? This overwrites the current list.`)) return
      const n = serviceLibReplace(rows)
      this.render()
      window.alert(`Imported ${n} service${n === 1 ? "" : "s"}.`)
    }
    reader.readAsText(file)
  }

  // ---- rendering ----

  _esc(v) { return String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;") }

  _catHtml(cat, items) {
    const esc = (v) => this._esc(v)
    return `
      <div style="margin-bottom:14px;">
        <div style="font:700 11px/1 inherit;text-transform:uppercase;letter-spacing:.04em;color:#64748b;margin-bottom:6px;">${esc(cat)}</div>
        ${items.map(s => this._rowHtml(s)).join("")}
      </div>`
  }

  _rowHtml(s) {
    const esc = (v) => this._esc(v)
    return `
      <div style="display:flex;align-items:center;gap:10px;border:1px solid #e2e8f0;border-radius:9px;padding:9px 12px;margin-bottom:6px;background:#fff;">
        <strong style="font-size:13.5px;color:#0f172a;">${esc(s.name)}</strong>
        <span style="margin-left:auto;display:flex;gap:6px;">
          <button type="button" data-action="settings-services#edit" data-name="${esc(s.name)}"
                  style="padding:5px 12px;font:600 12px/1 inherit;background:#fff;color:#334155;border:1px solid #cbd5e1;border-radius:7px;cursor:pointer;">Edit</button>
          <button type="button" data-action="settings-services#remove" data-name="${esc(s.name)}"
                  style="padding:5px 12px;font:600 12px/1 inherit;background:#fff;color:#b91c1c;border:1px solid #fecaca;border-radius:7px;cursor:pointer;">Delete</button>
        </span>
      </div>`
  }

  // ---- add / edit modal ----

  _openModal(svc, isEdit) {
    const esc = (v) => this._esc(v)
    const inputCss = "width:100%;padding:9px 11px;font:14px/1.4 inherit;border:1px solid #cbd5e1;border-radius:8px;box-sizing:border-box;color:#0f172a;background:#fff;"
    const labelCss = "display:block;font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.04em;margin:12px 0 6px;"
    const cats = serviceLibCategories()
    const overlay = document.createElement("div")
    overlay.setAttribute("role", "dialog"); overlay.setAttribute("aria-modal", "true")
    overlay.style.cssText = "position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(15,23,42,.55);backdrop-filter:blur(2px);font:14px/1.4 'Inter',ui-sans-serif,system-ui,sans-serif;"
    overlay.innerHTML = `
      <div style="width:min(94vw,460px);background:#fff;border-radius:14px;box-shadow:0 24px 48px rgba(0,0,0,.32);padding:18px 20px 18px;">
        <h3 style="margin:0;font-size:16px;font-weight:800;color:#0f172a;">${isEdit ? "Edit service" : "Add a service"}</h3>
        <p style="margin:6px 0 4px;font-size:12.5px;color:#64748b;">Shows under its sub-section in every resource's Service dropdown.</p>
        <label style="${labelCss}">Service name</label>
        <input data-f="name" value="${esc(svc.name)}" style="${inputCss}">
        <label style="${labelCss}">Sub-section</label>
        <input data-f="category" value="${esc(svc.category || "Other")}" list="settings-svc-cats" style="${inputCss}">
        <datalist id="settings-svc-cats">${cats.map(c => `<option value="${esc(c)}"></option>`).join("")}</datalist>
        <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:16px;">
          <button type="button" data-role="cancel" style="padding:8px 14px;font:600 13px/1 inherit;background:#f1f5f9;color:#334155;border:1px solid #e2e8f0;border-radius:8px;cursor:pointer;">Cancel</button>
          <button type="button" data-role="save" style="padding:8px 16px;font:700 13px/1 inherit;background:#4f46e5;color:#fff;border:1px solid #4f46e5;border-radius:8px;cursor:pointer;">${isEdit ? "Save" : "Add"}</button>
        </div>
      </div>`
    document.body.appendChild(overlay)
    const val = (n) => { const e = overlay.querySelector(`[data-f="${n}"]`); return e ? e.value : "" }
    const close = () => { document.removeEventListener("keydown", onKey); overlay.remove() }
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); close() } }
    document.addEventListener("keydown", onKey)
    overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
    overlay.querySelector('[data-role="cancel"]').addEventListener("click", close)
    overlay.querySelector('[data-role="save"]').addEventListener("click", () => {
      const name = val("name").trim()
      const category = val("category").trim() || "Other"
      if (!name) { const n = overlay.querySelector('[data-f="name"]'); if (n) n.focus(); return }
      if (isEdit) serviceLibUpdate(svc.name, name, category)
      else serviceLibAdd(name, category)
      close(); this.render()
    })
    setTimeout(() => { const n = overlay.querySelector('[data-f="name"]'); if (n) { n.focus(); n.select() } }, 0)
  }
}
